import { Download, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SmoothScroll } from "../components/SmoothScroll";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useLang } from "../i18n/LangContext";

const ASSETS = [
  { group: "logo", items: [
    { file: "allure-house-instagram-1080.png", fr: "Photo de profil Instagram", en: "Instagram profile picture", size: "1080 × 1080" },
    { file: "allure-house-logo-mark-dark-1080.png", fr: "Monogramme — fond sombre", en: "Monogram — dark background", size: "1080 × 1080" },
    { file: "allure-house-logo-mark-transparent-1080.png", fr: "Monogramme — fond transparent", en: "Monogram — transparent", size: "1080 × 1080", checker: true },
    { file: "og-image.png", fr: "Image de partage (liens)", en: "Link share image", size: "1200 × 630" },
  ] },
  { group: "posts", items: [
    { file: "post-manifeste-fr.png", fr: "Manifeste (FR)", en: "Manifesto (FR)", size: "1080 × 1080" },
    { file: "post-manifesto-en.png", fr: "Manifeste (EN)", en: "Manifesto (EN)", size: "1080 × 1080" },
    { file: "post-expertise-marketing.png", fr: "Expertise — Marketing", en: "Expertise — Marketing", size: "1080 × 1080" },
    { file: "post-expertise-web.png", fr: "Expertise — Sites web", en: "Expertise — Websites", size: "1080 × 1080" },
    { file: "post-expertise-marque.png", fr: "Expertise — Marque", en: "Expertise — Brand", size: "1080 × 1080" },
    { file: "post-trois-maisons.png", fr: "Trois maisons", en: "Three houses", size: "1080 × 1080" },
  ] },
  { group: "stories", items: [
    { file: "story-manifeste-fr.png", fr: "Story — Manifeste (FR)", en: "Story — Manifesto (FR)", size: "1080 × 1920", tall: true },
    { file: "story-manifesto-en.png", fr: "Story — Manifeste (EN)", en: "Story — Manifesto (EN)", size: "1080 × 1920", tall: true },
    { file: "story-expertises.png", fr: "Story — Expertises", en: "Story — Expertise", size: "1080 × 1920", tall: true },
  ] },
];

const COPY = {
  fr: { title: "Kit de", title2: "marque.", sub: "Logos et visuels Instagram prêts à publier. Cliquez pour télécharger en pleine résolution.", back: "Retour à l'accueil", dl: "Télécharger",
    groups: { logo: "Logos & identité", posts: "Posts Instagram — 1080 × 1080", stories: "Stories Instagram — 1080 × 1920" },
    tip: "Astuce : sur Instagram, utilisez le monogramme en photo de profil et alternez posts manifeste / expertises pour une grille cohérente." },
  en: { title: "Brand", title2: "kit.", sub: "Logos and ready-to-post Instagram visuals. Click to download in full resolution.", back: "Back to home", dl: "Download",
    groups: { logo: "Logos & identity", posts: "Instagram posts — 1080 × 1080", stories: "Instagram stories — 1080 × 1920" },
    tip: "Tip: on Instagram, use the monogram as your profile picture and alternate manifesto / expertise posts for a consistent grid." },
};

const Asset = ({ a, lang, dl, i }) => (
  <Reveal delay={Math.min(i * 0.05, 0.3)}>
    <a href={`/brand/${a.file}`} download data-testid={`brand-asset-${a.file.replace(/\.png$/, "")}`} className="group block">
      <div className={`overflow-hidden border border-line ${a.tall ? "aspect-[9/16]" : a.file === "og-image.png" ? "aspect-[1200/630]" : "aspect-square"} ${a.checker ? "bg-[repeating-conic-gradient(#2a2724_0_25%,#1a1917_0_50%)] bg-[length:24px_24px]" : "bg-ink-2"}`}>
        <img src={`/brand/${a.file}`} alt={a[lang]} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
      </div>
      <div className="mt-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm">{a[lang]}</p>
          <p className="font-mono text-[11px] tracking-wider text-stone mt-1">{a.size} · PNG</p>
        </div>
        <span className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.2em] uppercase text-gold opacity-70 group-hover:opacity-100 transition-opacity duration-300"><Download size={13} /> {dl}</span>
      </div>
    </a>
  </Reveal>
);

export default function BrandKit() {
  const { lang } = useLang();
  const c = COPY[lang];
  return (
    <div className="grain" data-testid="brand-kit-page">
      <Seo title={`${c.title} ${c.title2} | ALLURE HOUSE`} description={c.sub} noindex />
      <SmoothScroll />
      <Header />
      <main className="px-6 md:px-12 pt-32 md:pt-40 pb-28">
        <Link to="/" data-testid="brand-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-stone hover:text-gold transition-colors duration-300 mb-10">
          <ArrowLeft size={14} /> {c.back}
        </Link>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight" data-testid="brand-title">
          {c.title} <em className="text-gold italic font-normal">{c.title2}</em>
        </h1>
        <p className="mt-6 max-w-xl text-stone leading-relaxed">{c.sub}</p>
        <p className="mt-4 max-w-xl text-sm text-stone border-l border-[var(--gold)] pl-4">{c.tip}</p>
        {ASSETS.map((g) => (
          <section key={g.group} className="mt-20" data-testid={`brand-group-${g.group}`}>
            <p className="eyebrow mb-8">{c.groups[g.group]}</p>
            <div className={`grid gap-8 ${g.group === "stories" ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"}`}>
              {g.items.map((a, i) => <Asset key={a.file} a={a} lang={lang} dl={c.dl} i={i} />)}
            </div>
          </section>
        ))}
      </main>
      <Footer />
    </div>
  );
}
